/**
 * Daily work typed on this phone and not saved yet, kept on the device per studio, user and day, so a
 * reload, a date change or a dropped connection never loses a row. Only what was typed is kept
 * (inches, people, part, the request id that makes a retry safe); never in the URL. The Daily work
 * list and the loom page (phone page, iPad pane, desktop drawer) share it, one line per loom.
 */
import type { SheetLine } from "../sheet/sheetModel";

export type DraftPerson = { id: string; code: string; name: string };

export type DraftLine = Pick<SheetLine, "loomId" | "sareeNumber" | "weaverId" | "helperId" | "helperMode" | "workTypeId" | "inches" | "requestId" | "message"> & {
  status: "EDIT" | "ERROR";
  /** The weaver and helper were confirmed or changed on this screen. */
  confirmed: boolean;
  /** Loom page only: typed in girah, part of it woven alone, or with ladka sool. The list can't show these. */
  unit?: "INCH" | "GIRAH";
  alone?: string;
  sool?: boolean;
  /** Loom page only: a second (third…) part woven the same day, row 1, 2… under the first. */
  row?: number;
};

export type DailyDraft = { v: 1; savedAt: number; lines: DraftLine[]; people?: DraftPerson[] };

const PREFIX = "hkp.karkhana.daily.";
export const DRAFT_MAX_AGE_DAYS = 30;

const store = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * Whose drafts these are: studio + signed-in user ("<orgId>.<userId>"). Null when nobody is signed in
 * (after a logout nothing is written, and the next person on this phone never sees these rows).
 */
export const draftScope = (): string | null => {
  try {
    const u = JSON.parse(store()?.getItem("authUser") || "null")?.users;
    if (!u) return null;
    const org = u.organizationResponse?._id || "org";
    const me = u._id || u.id || u.userId || u.email || u.mobile || "me";
    return `${org}.${me}`;
  } catch {
    return null;
  }
};

export const draftKey = (scope: string, date: string) => `${PREFIX}${scope}.${date}`;

/** Lines worth keeping: something typed and not saved (a failed save stays, with its reason). */
export const draftLines = (lines: SheetLine[], confirmed: Set<string>): DraftLine[] =>
  lines
    .filter((l) => (l.status === "EDIT" || l.status === "ERROR" || l.status === "SAVING") && l.inches.trim() !== "")
    .map((l) => ({
      loomId: l.loomId,
      sareeNumber: l.sareeNumber,
      weaverId: l.weaverId,
      helperId: l.helperId,
      helperMode: l.helperMode,
      workTypeId: l.workTypeId,
      inches: l.inches,
      requestId: l.requestId,
      // A save cut off mid-way is retried with the same request id, so it can't save twice.
      status: l.status === "ERROR" ? "ERROR" : "EDIT",
      message: l.status === "ERROR" ? l.message : undefined,
      confirmed: confirmed.has(l.loomId),
    }));

export const readDraft = (scope: string, date: string): DailyDraft | null => {
  try {
    const raw = store()?.getItem(draftKey(scope, date));
    const d = raw ? JSON.parse(raw) : null;
    return d && d.v === 1 && Array.isArray(d.lines) && d.lines.length ? d : null;
  } catch {
    return null;
  }
};

/** Typed on the loom page with something the list can't carry (girah, part alone, ladka sool). */
export const loomPageOnly = (d: DraftLine) => !!d.row || d.unit === "GIRAH" || !!(d.alone && d.alone.trim()) || !!d.sool;

/**
 * Where a line lives in the day's copy. Plain inches share the loom's slot (the list and the loom page
 * show the same row); a loom-page-only line (girah, part alone, ladka sool) has its own slot "<loom>#page",
 * so typing in the list never overwrites it, and both are kept.
 */
export const draftSlot = (d: DraftLine) => (d.row ? rowSlot(d.loomId, d.row) : loomPageOnly(d) ? pageSlot(d.loomId) : d.loomId);
export const pageSlot = (loomId: string) => `${loomId}#page`;
/** An added part row on the loom page ("+ Add another part"): its own slot, "<loom>#row<n>". */
export const rowSlot = (loomId: string, row: number) => `${loomId}#row${row}`;

/**
 * Writes this screen's typed lines for the day, merged by slot with what is stored: slots this screen
 * didn't type stay as they are (another tab, or the loom page), `drop` removes the slots (loom ids, or
 * `pageSlot(id)`) this screen saved, cleared or discarded. The day's copy is removed when nothing is left.
 */
export const writeDraft = (scope: string, date: string, lines: DraftLine[], people: DraftPerson[] = [], drop: string[] = [], now = Date.now()) => {
  try {
    const s = store();
    if (!s) return;
    const mine = new Set([...lines.map(draftSlot), ...drop]);
    const before = readDraft(scope, date);
    const merged = [...(before?.lines || []).filter((l) => !mine.has(draftSlot(l))), ...lines];
    if (!merged.length) {
      s.removeItem(draftKey(scope, date));
      return;
    }
    const used = new Set(merged.flatMap((l) => [l.weaverId, l.helperId]).filter(Boolean));
    const byId = new Map([...(before?.people || []), ...people].map((p) => [p.id, p]));
    const draft: DailyDraft = { v: 1, savedAt: now, lines: merged, people: Array.from(byId.values()).filter((p) => used.has(p.id)) };
    s.setItem(draftKey(scope, date), JSON.stringify(draft));
  } catch {
    // Storage full or blocked: the rows stay on screen, only the device copy is missing.
  }
};

/** Changes (or with null removes) one loom's shared line in a day's copy: a save that finished after the list moved on. */
export const patchDraftLine = (scope: string, date: string, loomId: string, patch: Partial<DraftLine> | null) => {
  const line = readDraft(scope, date)?.lines.find((l) => draftSlot(l) === loomId);
  if (!patch) writeDraft(scope, date, [], [], [loomId]);
  else if (line) writeDraft(scope, date, [{ ...line, ...patch }]);
};

/**
 * Changes (or with null removes) the loom's line(s) for one piece of work (its request id), in whichever
 * slot: the loom page saving, failing or discarding what it typed, never another screen's other work.
 */
export const patchDraftRequest = (scope: string, date: string, loomId: string, requestId: string, patch: Partial<DraftLine> | null) => {
  const lines = (readDraft(scope, date)?.lines || []).filter((l) => l.loomId === loomId && l.requestId === requestId);
  if (!lines.length) return;
  if (!patch) writeDraft(scope, date, [], [], lines.map(draftSlot));
  else writeDraft(scope, date, lines.map((l) => ({ ...l, ...patch })));
};

export const clearDraft = (scope: string, date: string) => {
  try {
    store()?.removeItem(draftKey(scope, date));
  } catch {
    // nothing to clear
  }
};

/** Other days of this studio and user with unsaved rows on this device: [{date, n}], newest day first. */
export const otherDrafts = (scope: string, except: string): { date: string; n: number }[] => {
  const s = store();
  if (!s) return [];
  const out: { date: string; n: number }[] = [];
  try {
    const prefix = `${PREFIX}${scope}.`;
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      const date = k.slice(prefix.length);
      if (date === except || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const d = readDraft(scope, date);
      if (d) out.push({ date, n: d.lines.length });
    }
  } catch {
    return out;
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : -1));
};

/**
 * Screens with typing not written yet register here, so a session that expires (the app clears storage
 * and goes to the login page) can write them first. Returns the unregister function.
 */
const flushers = new Set<() => void>();
export const registerDraftFlush = (fn: () => void) => {
  flushers.add(fn);
  return () => {
    flushers.delete(fn);
  };
};
export const flushAllDrafts = () =>
  flushers.forEach((fn) => {
    try {
      fn();
    } catch {
      // one screen failing must not stop the others
    }
  });

/** Storage keys of every Daily work copy on this phone (all users): kept through a session expiry. */
export const DRAFT_KEY_PREFIX = PREFIX;

/** All unsaved rows this user has on this phone (any day): asked about before logging out. */
export const unsavedDraftCount = (scope: string | null): number => {
  if (!scope) return 0;
  const s = store();
  let n = 0;
  try {
    const prefix = `${PREFIX}${scope}.`;
    for (let i = 0; s && i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(prefix)) n += readDraft(scope, k.slice(prefix.length))?.lines.length || 0;
    }
  } catch {
    return n;
  }
  return n;
};

/** Drops drafts (any studio or user) not touched for 30 days, and unreadable ones. */
export const cleanOldDrafts = (now = Date.now(), maxAgeDays = DRAFT_MAX_AGE_DAYS) => {
  const s = store();
  if (!s) return;
  try {
    const stale: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      let savedAt = 0;
      try {
        savedAt = Number(JSON.parse(s.getItem(k) || "null")?.savedAt) || 0;
      } catch {
        savedAt = 0;
      }
      if (now - savedAt > maxAgeDays * 864e5) stale.push(k);
    }
    stale.forEach((k) => s.removeItem(k));
  } catch {
    // storage blocked
  }
};

/**
 * The parts the server already has for each loom that day ({loomId: Set<workTypeId>}), from the rounds
 * response. A phone copy is a conflict only when its part is already saved there: a second part woven
 * the same day is a different entry and is never thrown away.
 */
export const savedPartsByLoom = (looms: any[] | undefined): Map<string, Set<string>> =>
  new Map(
    (looms || []).map((l: any) => [
      String(l.id),
      new Set<string>((l.entries || []).filter((e: any) => e.status !== "VOID" && e.status !== "REJECTED").map((e: any) => String(e.workTypeId || ""))),
    ])
  );

/** True when the server already has this loom's entry for the day on the copy's part (or on an unknown part). */
export const partAlreadySaved = (d: DraftLine, saved: Set<string> | undefined): boolean =>
  !!saved && saved.size > 0 && (!d.workTypeId || saved.has(d.workTypeId) || saved.has(""));

/**
 * Puts the device copy back on the lines the server sent for that day. A loom the server now has
 * saved for the day on the same part (someone else saved it, or our own save went through before the
 * reload) keeps the server's entry and drops the device copy (`alreadySaved`); a different part saved
 * that day leaves the copy stored for the loom page; a loom whose saree changed drops it
 * too (`changedSaree`); never applied silently. A loom with no line any more (closed, no saree) is
 * counted in `gone`. Lines typed on the loom page with girah / alone / ladka sool are left for that
 * page (`skipped`): the list can't show or save them.
 */
export const applyDraft = (
  fresh: SheetLine[],
  draft: DailyDraft | null,
  savedParts?: Map<string, Set<string>>
): {
  lines: SheetLine[];
  confirmed: Set<string>;
  restored: number;
  /** The looms put back: Discard removes only these. */
  restoredIds: string[];
  alreadySaved: string[];
  changedSaree: string[];
  gone: number;
  dropped: string[];
} => {
  const confirmed = new Set<string>();
  const restoredIds: string[] = [];
  const none = { lines: fresh, confirmed, restored: 0, restoredIds, alreadySaved: [], changedSaree: [], gone: 0, dropped: [] };
  if (!draft) return none;
  const byLoom = new Map(draft.lines.filter((d) => !loomPageOnly(d)).map((d) => [d.loomId, d]));
  const alreadySaved: string[] = [];
  const changedSaree: string[] = [];
  const dropped: string[] = [];
  let restored = 0;
  const lines = fresh.map((l) => {
    const d = byLoom.get(l.loomId);
    if (!d) return l;
    byLoom.delete(l.loomId);
    if (l.status === "SAVED") {
      // Same part already saved that day: the server's entry wins. A different part (a second entry the
      // same day) stays on the phone for the loom page, where a second part is entered.
      if (!savedParts || partAlreadySaved(d, savedParts.get(l.loomId))) {
        alreadySaved.push(l.loomCode);
        dropped.push(l.loomId);
      }
      return l;
    }
    if (String(d.sareeNumber) !== String(l.sareeNumber)) {
      changedSaree.push(l.loomCode);
      dropped.push(l.loomId);
      return l;
    }
    restored++;
    restoredIds.push(l.loomId);
    if (d.confirmed) confirmed.add(l.loomId);
    return {
      ...l,
      weaverId: d.weaverId,
      helperId: d.helperId,
      helperMode: d.helperMode,
      workTypeId: d.workTypeId || l.workTypeId,
      inches: d.inches,
      requestId: d.requestId || l.requestId,
      status: d.status,
      message: d.message,
    };
  });
  // Looms that no longer take an entry on this day (closed, saree gone): counted, not applied.
  byLoom.forEach((d) => dropped.push(d.loomId));
  return { lines, confirmed, restored, restoredIds, alreadySaved, changedSaree, gone: byLoom.size, dropped };
};
